import type { ReactNode } from "react";
import { BrandLogo } from "@rekati/ui";
import { IFORTE_LOGO } from "../../assets/brand";
import { useI18n } from "../../content/i18n";
import { useBrandQuery } from "../event/queries";

/**
 * The organiser as host, then "Powered by" the partner and the platform
 * (decision 60, 84): three logos on one line, each in a strip of the same
 * height so they share a centre line whatever the files' proportions. The
 * platform's file carries wide margins, so it gets the tallest box. The row
 * is centred. Labels never wrap: on a phone they get smaller instead, so both
 * stay one line and the logos under them stay level, down to 320px.
 */
export function SiteFooter() {
  const { t } = useI18n();
  const brand = useBrandQuery().data;
  const partner = brand?.powered_by_name || brand?.powered_by_logo_url;
  return (
    <footer className="border-t border-border bg-background px-4 py-10 sm:px-8">
      <div className="mx-auto flex max-w-7xl flex-wrap items-end justify-center gap-x-4 gap-y-6 min-[375px]:gap-x-5 sm:gap-x-10">
        <Group label={t("brand.organisedBy")}>
          <BrandLogo
            src={brand?.organizer_logo_url || brand?.organizer_logo_dark_url}
            fallbackSrc={IFORTE_LOGO}
            name={brand?.organizer_name || "iForte"}
            className="h-5 shrink-0 min-[375px]:h-6 sm:h-7"
            textClassName="text-base font-bold"
          />
        </Group>
        <span className="mb-1.5 hidden h-6 w-px bg-border sm:block" aria-hidden="true" />
        <Group label={t("brand.poweredBy")}>
          {partner ? (
            <>
              <BrandLogo
                src={brand.powered_by_logo_url || brand.powered_by_logo_dark_url}
                name={brand.powered_by_name}
                className="h-4 shrink-0 min-[375px]:h-5 sm:h-6"
                textClassName="text-base"
              />
              <span className="h-6 w-px shrink-0 bg-border" aria-hidden="true" />
            </>
          ) : null}
          <BrandLogo
            src={brand?.platform_logo_url}
            name={brand?.platform_name || "Rekati"}
            className="h-7 shrink-0 min-[375px]:h-8 sm:h-9"
            textClassName="font-mono text-sm"
          />
        </Group>
      </div>
    </footer>
  );
}

function Group({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid justify-items-start gap-2">
      <span className="font-mono text-[0.5rem] leading-tight tracking-[0.04em] whitespace-nowrap text-muted-foreground uppercase min-[375px]:text-[0.55rem] min-[375px]:tracking-[0.06em] min-[440px]:text-[0.62rem] min-[440px]:tracking-[0.16em] sm:text-[0.68rem]">
        {label}
      </span>
      <div className="flex h-9 w-max items-center gap-2 min-[375px]:gap-3 sm:gap-4">{children}</div>
    </div>
  );
}
